import { useCallback, useRef, useState } from 'react';
import { trpc } from '@org/clients/school';
import { Sentry, isLikelyTranscriptionArtifact } from '@org/utils-client';
import type { CompletionResult, ConversationTurn } from './conversational-exercise-types';

// ---------------------------------------------------------------------------
// Tunables
// ---------------------------------------------------------------------------

/**
 * Idle windows count TRUE inactivity only: the clock is paused while the
 * student holds the mic and while the AI is answering or speaking, so a long
 * answer or a long reply can never trigger them.
 */
const IDLE_PROMPT_MS = 90_000;
const IDLE_END_MS = 150_000;
/** The "call ends in Ns" countdown is shown only during this last stretch. */
const IDLE_COUNTDOWN_VISIBLE_MS = 60_000;

/** Shorter recordings are near-silence: cleared, never sent (Whisper hallucinates on them). */
const MIN_RECORDING_MS = 700;
/** Below this share of "voiced" samples a recording is treated as silence. */
const MIN_VOICED_RATIO = 0.08;
/** RMS amplitude (0..1) above which a 50 ms slice counts as voice. ~ -40 dBFS. */
const VOICE_RMS_THRESHOLD = 0.01;
const LEVEL_SAMPLE_MS = 50;
/** RMS that counts as "full bars" in the waveform UI (see getLevels). */
const LEVEL_FULL_SCALE_RMS = 0.18;
/** Audio travels on a different stream than the data channel; let the tail arrive before committing. */
const COMMIT_TAIL_MS = 300;

/** When the countdown reaches this, the AI is asked to recap and say goodbye. */
const WRAP_UP_AT_SEC = 60;
/** The AI's own end_conversation is honoured only in the last fifth of the time (or after a goodbye). */
const AI_END_HONOUR_RATIO = 0.2;
/** Max wait for the AI's closing audio to finish before tearing the call down. */
const AUDIO_DRAIN_TIMEOUT_MS = 8_000;
/** How long a 'disconnected' WebRTC state may last before we end the call. */
const DISCONNECT_GRACE_MS = 5_000;
/** After a hold-to-talk release, ignore the synthetic click that follows. */
const SUPPRESS_CLICK_MS = 400;

const GOODBYE_PATTERN =
  /\b(bye|goodbye|see you|that'?s all|i have to go|i need to go|let'?s stop|we can stop|can we stop|stop (the )?conversation|stop here|i'?m done|finish(ed)? (for )?today|tchau|at[eé] (logo|mais|a próxima)|acabou|posso parar|vamos parar|quero parar)\b/i;

const IDLE_NUDGE_INSTRUCTIONS =
  'The student has not pressed the talk button for a while. She may be thinking, reading, or having trouble with the button. Gently and briefly check in — for example "Take your time. Whenever you are ready, press the button and tell me…" — and repeat your last question in a simpler way. Do NOT say she is silent, do NOT say goodbye and do NOT call end_conversation.';

const WRAP_UP_SYSTEM_NOTE =
  'TIME CHECK: about one minute of the session is left. In your next turn give a warm, short closing: recap two or three things the student practised well (quote her words), mention one thing to work on, say goodbye, and only after you have finished speaking call end_conversation.';

const WRAP_UP_INSTRUCTIONS =
  'Time is almost up. Give a warm, short closing now: recap two or three things the student practised well (quote her words), mention one thing to work on, say goodbye, and only after you have finished speaking call end_conversation.';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ConversationEndReason = 'user' | 'timer' | 'idle' | 'ai' | 'connection' | 'unmount';

export interface ConversationEndOutcome {
  reason: ConversationEndReason;
  result?: CompletionResult;
  error?: Error;
}

interface UseOpenAIRealtimeOptions {
  maxDurationSec: number;
  getMicConstraints: (additionalConstraints?: MediaTrackConstraints) => MediaStreamConstraints;
  setSpeakerOnElement: (el: HTMLAudioElement | HTMLVideoElement) => Promise<boolean>;
  /** Fires once when the call starts ending, whatever triggered it, so the UI can show "Ending…". */
  onEnding?: (reason: ConversationEndReason) => void;
  /** Fires once with the final result or error, for EVERY end path (timer, idle, AI, click, connection loss). */
  onEnded?: (outcome: ConversationEndOutcome) => void;
}

export interface OpenAIRealtimeState {
  turns: ConversationTurn[];
  currentTranscript: string;
  timeRemaining: number;
  isRecording: boolean;
  error: string | null;
  /** Seconds until the call ends for inactivity; only set during the last minute, otherwise null. */
  idleSecondsLeft: number | null;
  /**
   * Whole seconds the line has been quiet. Off-mic: nobody recording, no AI response in
   * flight, no AI audio playing. On-mic: seconds since the open mic last picked up voice.
   * 0 the moment anyone talks. Lets the UI nudge a hesitant student ("Go ahead, say
   * something") long before the inactivity countdown.
   */
  quietSeconds: number;
  /** True once the AI was asked to wrap up (about one minute left). */
  isWrappingUp: boolean;
  /** True while the AI's voice is playing (output_audio_buffer started → stopped/cleared). */
  isAiSpeaking: boolean;
  /** True from the moment a response is requested until its audio starts (or the response ends). */
  isAiThinking: boolean;
  /**
   * What the AI is saying in the current response, growing word by word as the
   * transcript streams in (ahead of the voice). Empty between responses.
   */
  aiLiveTranscript: string;
}

export interface OpenAIRealtimeActions {
  /** Connect to OpenAI Realtime API with session credentials */
  connect: (session: { sessionId: string; ephemeralKey: string }) => Promise<void>;
  /**
   * End the conversation and get the evaluation. Guarded: the first caller runs
   * the end flow, every later caller gets the same promise.
   */
  endConversation: (reason?: ConversationEndReason) => Promise<CompletionResult>;
  /** Tear down audio/WebRTC without ending the session on the server (use on fatal start errors). */
  cleanup: () => void;
  /** Clear a sticky error banner. */
  clearError: () => void;
  /** True while a session is connected and has not started ending. */
  isActive: () => boolean;
  /** Toggle recording on/off (tap mode) */
  handleToggleRecording: () => void;
  /** Start hold-to-speak on pointer down */
  handlePointerDown: () => void;
  /** Stop hold-to-speak on pointer up */
  handlePointerUp: () => void;
  /**
   * Current loudness of the student's mic and of the AI's voice, 0..1, read
   * straight from the analysers (no React state): call it from a
   * requestAnimationFrame loop to drive waveforms. Both are 0 when unavailable.
   */
  getLevels: () => { mic: number; ai: number };
}

type RealtimeEvent = Record<string, unknown>;

type EndSessionResult = {
  score: number;
  maxScore: number;
  passingScore: number;
  passed: boolean;
  feedback: string;
  skillAssessments: Array<{ skill?: string; score?: number; feedback?: string }>;
  corrections: Array<{ studentQuote: string; correctedForm: string; habit: string; explanation: string }>;
  evaluator: 'ai' | 'manual' | 'none';
  lowContent: boolean;
  recovered: boolean;
};

function toCompletionResult(result: EndSessionResult): CompletionResult {
  return {
    score: result.score,
    maxScore: result.maxScore,
    passingScore: result.passingScore,
    passed: result.passed,
    feedback: result.feedback,
    skillAssessments: result.skillAssessments,
    corrections: result.corrections ?? [],
    evaluator: result.evaluator,
    lowContent: result.lowContent,
    recovered: result.recovered,
  };
}

function computeRms(samples: Float32Array): number {
  let sum = 0;
  for (let i = 0; i < samples.length; i += 1) sum += samples[i] * samples[i];
  return Math.sqrt(sum / Math.max(1, samples.length));
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useOpenAIRealtime(
  options: UseOpenAIRealtimeOptions
): OpenAIRealtimeState & OpenAIRealtimeActions {
  const { maxDurationSec } = options;

  // Latest options/mutations live in refs so every returned function can be stable.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const [turns, setTurns] = useState<ConversationTurn[]>([]);
  const [currentTranscript, setCurrentTranscript] = useState('');
  const [timeRemaining, setTimeRemaining] = useState(maxDurationSec);
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [idleSecondsLeft, setIdleSecondsLeft] = useState<number | null>(null);
  const [quietSeconds, setQuietSeconds] = useState(0);
  const [isWrappingUp, setIsWrappingUp] = useState(false);
  const [isAiSpeaking, setIsAiSpeaking] = useState(false);
  const [isAiThinking, setIsAiThinking] = useState(false);
  const [aiLiveTranscript, setAiLiveTranscript] = useState('');
  const aiLiveRef = useRef('');
  const aiLiveFlushRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Transcript deltas arrive many times a second; batch them into one state update per ~100 ms.
  const pushAiLive = useCallback((delta: string) => {
    aiLiveRef.current += delta;
    if (aiLiveFlushRef.current) return;
    aiLiveFlushRef.current = setTimeout(() => {
      aiLiveFlushRef.current = null;
      setAiLiveTranscript(aiLiveRef.current);
    }, 100);
  }, []);
  const resetAiLive = useCallback((text = '') => {
    if (aiLiveFlushRef.current) {
      clearTimeout(aiLiveFlushRef.current);
      aiLiveFlushRef.current = null;
    }
    aiLiveRef.current = text;
    setAiLiveTranscript(text);
  }, []);

  // WebRTC and media
  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const dataChannelRef = useRef<RTCDataChannel | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);

  // Loudness check (Web Audio analyser on the mic stream)
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  /** Analyser on the AI's remote audio track (waveform only; shares the mic AudioContext). */
  const remoteAnalyserRef = useRef<AnalyserNode | null>(null);
  const levelBuffersRef = useRef<{ mic: Float32Array<ArrayBuffer> | null; ai: Float32Array<ArrayBuffer> | null }>({ mic: null, ai: null });
  const levelIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const voicedSamplesRef = useRef(0);
  const totalSamplesRef = useRef(0);
  /** Last time the open mic picked up voice; drives quietSeconds while recording. */
  const lastVoiceAtRef = useRef(0);
  const lastQuietEmittedRef = useRef(0);

  // Countdown + idle
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeRemainingRef = useRef(maxDurationSec);
  const idleIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const idleMsRef = useRef(0);
  const hasPromptedIdleRef = useRef(false);
  const nudgePendingRef = useRef(false);
  const nudgeResponseIdRef = useRef<string | null>(null);
  const wrapUpSentRef = useRef(false);
  const disconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Response / audio state reported by the API
  const activeResponseIdRef = useRef<string | null>(null);
  const aiSpeakingRef = useRef(false);
  const responseQueuedRef = useRef(false);
  const awaitingCommitRef = useRef(false);
  const commitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const audioDrainWaitersRef = useRef<Array<() => void>>([]);

  // Hold-to-speak
  const isHoldingRef = useRef(false);
  const holdTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const recordingStartTimeRef = useRef<number | null>(null);
  const isRecordingRef = useRef(false);
  const suppressClickUntilRef = useRef(0);

  // Session + end flow
  const sessionIdRef = useRef<string | null>(null);
  const endedRef = useRef(false);
  const endingPromiseRef = useRef<Promise<CompletionResult> | null>(null);
  const pendingSavesRef = useRef<Set<Promise<unknown>>>(new Set());
  const sequenceRef = useRef(0);
  const turnsRef = useRef<ConversationTurn[]>([]);

  // tRPC
  const utils = trpc.useUtils();
  const endSessionMutation = trpc.conversation.endSession.useMutation();
  const saveTurnMutation = trpc.conversation.saveTurn.useMutation();
  const endSessionMutationRef = useRef(endSessionMutation);
  endSessionMutationRef.current = endSessionMutation;
  const saveTurnMutationRef = useRef(saveTurnMutation);
  saveTurnMutationRef.current = saveTurnMutation;

  // --- Small helpers -------------------------------------------------------

  const send = useCallback((event: RealtimeEvent) => {
    const dc = dataChannelRef.current;
    if (dc && dc.readyState === 'open') {
      dc.send(JSON.stringify(event));
      return true;
    }
    return false;
  }, []);

  const breadcrumb = useCallback((message: string, data?: Record<string, unknown>) => {
    Sentry.addBreadcrumb({
      category: 'conversation',
      level: 'info',
      message,
      data: { sessionId: sessionIdRef.current, ...data },
    });
  }, []);

  const updateTurns = useCallback((updater: (prev: ConversationTurn[]) => ConversationTurn[]) => {
    turnsRef.current = updater(turnsRef.current);
    setTurns(turnsRef.current);
  }, []);

  const resetIdle = useCallback(() => {
    idleMsRef.current = 0;
    hasPromptedIdleRef.current = false;
    setIdleSecondsLeft(null);
    setQuietSeconds(0);
  }, []);

  const resolveAudioDrain = useCallback(() => {
    const waiters = audioDrainWaitersRef.current;
    audioDrainWaitersRef.current = [];
    waiters.forEach((resolve) => resolve());
  }, []);

  /** Resolves once the AI has stopped speaking (or after a timeout). */
  const waitForAudioDrain = useCallback(() => {
    if (!aiSpeakingRef.current && !activeResponseIdRef.current) return Promise.resolve();
    return new Promise<void>((resolve) => {
      const timeout = setTimeout(() => {
        audioDrainWaitersRef.current = audioDrainWaitersRef.current.filter((w) => w !== done);
        resolve();
      }, AUDIO_DRAIN_TIMEOUT_MS);
      const done = () => {
        clearTimeout(timeout);
        resolve();
      };
      audioDrainWaitersRef.current.push(done);
    });
  }, []);

  /**
   * Ask the model to respond. Never while a response is in flight (that is the
   * `conversation_already_has_active_response` error Ana saw as a freeze): the
   * request is queued and sent on `response.done`.
   */
  const requestResponse = useCallback(
    (instructions?: string) => {
      if (activeResponseIdRef.current) {
        responseQueuedRef.current = true;
        return;
      }
      send({
        type: 'response.create',
        response: {
          output_modalities: ['audio'],
          ...(instructions ? { instructions } : {}),
        },
      });
    },
    [send]
  );

  const saveTurn = useCallback(
    (role: 'user' | 'assistant', content: string, itemId?: string) => {
      const sessionId = sessionIdRef.current;
      if (!sessionId) return;
      const sequence = sequenceRef.current;
      sequenceRef.current += 1;
      const promise = saveTurnMutationRef.current
        .mutateAsync({ sessionId, role, content, sequence })
        .then((saved) => {
          // The server may refuse a line it considers transcription noise: drop the bubble too.
          if (saved?.skipped && itemId) {
            updateTurns((prev) => prev.filter((turn) => turn.itemId !== itemId));
          }
        })
        .catch((err) => {
          Sentry.captureException(err, {
            level: 'warning',
            tags: { feature: 'conversational-exercise', stage: 'save-turn' },
            extra: { sessionId, role, sequence },
          });
        })
        .finally(() => {
          pendingSavesRef.current.delete(promise);
        });
      pendingSavesRef.current.add(promise);
    },
    [updateTurns]
  );

  // --- Loudness sampling ---------------------------------------------------

  const startLevelSampling = useCallback(() => {
    voicedSamplesRef.current = 0;
    totalSamplesRef.current = 0;
    const analyser = analyserRef.current;
    if (!analyser) return;
    void audioContextRef.current?.resume().catch(() => undefined);
    const buffer = new Float32Array(analyser.fftSize);
    lastVoiceAtRef.current = Date.now();
    lastQuietEmittedRef.current = 0;
    levelIntervalRef.current = setInterval(() => {
      analyser.getFloatTimeDomainData(buffer);
      totalSamplesRef.current += 1;
      if (computeRms(buffer) >= VOICE_RMS_THRESHOLD) {
        voicedSamplesRef.current += 1;
        lastVoiceAtRef.current = Date.now();
      }
      // Silence into an open mic is "quiet" too: the UI nudges "Go ahead, say something".
      const quiet = Math.floor((Date.now() - lastVoiceAtRef.current) / 1000);
      if (quiet !== lastQuietEmittedRef.current) {
        lastQuietEmittedRef.current = quiet;
        setQuietSeconds(quiet);
      }
    }, LEVEL_SAMPLE_MS);
  }, []);

  /** Stops sampling and returns the share of voiced slices, or null when no analyser was available. */
  const stopLevelSampling = useCallback((): number | null => {
    if (levelIntervalRef.current) {
      clearInterval(levelIntervalRef.current);
      levelIntervalRef.current = null;
    }
    if (!analyserRef.current || totalSamplesRef.current === 0) return null;
    return voicedSamplesRef.current / totalSamplesRef.current;
  }, []);

  // --- Teardown ------------------------------------------------------------

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (idleIntervalRef.current) {
      clearInterval(idleIntervalRef.current);
      idleIntervalRef.current = null;
    }
    if (commitTimeoutRef.current) {
      clearTimeout(commitTimeoutRef.current);
      commitTimeoutRef.current = null;
    }
    if (disconnectTimerRef.current) {
      clearTimeout(disconnectTimerRef.current);
      disconnectTimerRef.current = null;
    }
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }
    stopLevelSampling();
    if (audioContextRef.current) {
      void audioContextRef.current.close().catch(() => undefined);
      audioContextRef.current = null;
      analyserRef.current = null;
    }
    remoteAnalyserRef.current = null;
    levelBuffersRef.current = { mic: null, ai: null };
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    if (dataChannelRef.current) {
      dataChannelRef.current.onclose = null;
      dataChannelRef.current.close();
      dataChannelRef.current = null;
    }
    if (peerConnectionRef.current) {
      peerConnectionRef.current.onconnectionstatechange = null;
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }
    if (audioElementRef.current) {
      audioElementRef.current.srcObject = null;
      audioElementRef.current = null;
    }
    resolveAudioDrain();
    aiSpeakingRef.current = false;
    activeResponseIdRef.current = null;
    responseQueuedRef.current = false;
    awaitingCommitRef.current = false;
    hasPromptedIdleRef.current = false;
    nudgePendingRef.current = false;
    nudgeResponseIdRef.current = null;
    isHoldingRef.current = false;
    isRecordingRef.current = false;
    recordingStartTimeRef.current = null;
    setIsRecording(false);
    setIsAiSpeaking(false);
    setIsAiThinking(false);
    resetAiLive();
    setIdleSecondsLeft(null);
    setQuietSeconds(0);
    setCurrentTranscript('');
  }, [resolveAudioDrain, stopLevelSampling]);

  // --- The single, guarded end path ---------------------------------------

  const stopRecordingRef = useRef<(forceCancel?: boolean) => boolean>(() => false);

  const endConversation = useCallback(
    (reason: ConversationEndReason = 'user'): Promise<CompletionResult> => {
      if (endingPromiseRef.current) return endingPromiseRef.current;

      const sessionId = sessionIdRef.current;
      if (!sessionId) {
        return Promise.reject(new Error('No active session'));
      }

      const promise = (async () => {
        endedRef.current = true;
        optionsRef.current.onEnding?.(reason);
        breadcrumb(`end:${reason}`, {
          timeRemaining: timeRemainingRef.current,
          turns: turnsRef.current.length,
        });

        try {
          if (isRecordingRef.current) {
            // Drop a half-finished recording rather than sending noise.
            stopRecordingRef.current(true);
          }
          // Let the AI finish its goodbye before pulling the plug.
          if (reason === 'ai' || reason === 'timer') {
            await waitForAudioDrain();
          }
          cleanup();
          // Late transcripts are worth waiting for: the evaluation reads the saved turns.
          await Promise.allSettled(Array.from(pendingSavesRef.current));

          const raw = (await endSessionMutationRef.current.mutateAsync({ sessionId })) as EndSessionResult;
          const result = toCompletionResult(raw);
          optionsRef.current.onEnded?.({ reason, result });
          void utils.conversation.getSessions.invalidate();
          return result;
        } catch (err) {
          const endError = err instanceof Error ? err : new Error(String(err));
          Sentry.captureException(endError, {
            tags: { feature: 'conversational-exercise', stage: 'end', reason },
            extra: { sessionId },
          });
          optionsRef.current.onEnded?.({ reason, error: endError });
          void utils.conversation.getSessions.invalidate();
          throw endError;
        }
      })();

      endingPromiseRef.current = promise;
      // Avoid unhandled-rejection noise for callers that fire-and-forget (timers).
      promise.catch(() => undefined);
      return promise;
    },
    [breadcrumb, cleanup, utils, waitForAudioDrain]
  );

  const endConversationRef = useRef(endConversation);
  endConversationRef.current = endConversation;

  // --- AI-requested end ----------------------------------------------------

  const handleAiEndRequest = useCallback(
    (callId: string | undefined, args: unknown) => {
      const remaining = timeRemainingRef.current;
      const lastUserTurn = [...turnsRef.current].reverse().find((t) => t.role === 'user' && !t.pending);
      const saidGoodbye = !!lastUserTurn && GOODBYE_PATTERN.test(lastUserTurn.content);
      const nearEnd = wrapUpSentRef.current || remaining <= maxDurationSec * AI_END_HONOUR_RATIO;

      breadcrumb('ai-end-request', { remaining, saidGoodbye, nearEnd, args });

      if (saidGoodbye || nearEnd) {
        if (callId) {
          send({
            type: 'conversation.item.create',
            item: { type: 'function_call_output', call_id: callId, output: JSON.stringify({ status: 'ending' }) },
          });
        }
        void endConversationRef.current('ai');
        return;
      }

      // Too early: tell the model to keep going and ask it for the next turn.
      const minutesLeft = Math.max(1, Math.ceil(remaining / 60));
      if (callId) {
        send({
          type: 'conversation.item.create',
          item: {
            type: 'function_call_output',
            call_id: callId,
            output: JSON.stringify({
              status: 'continue',
              message: `Not yet: about ${minutesLeft} minute(s) remain and the student has not said goodbye. Do not end. Continue the conversation on the theme with a new question.`,
            }),
          },
        });
      }
      requestResponse();
    },
    [breadcrumb, maxDurationSec, requestResponse, send]
  );

  // --- Wrap-up at one minute left -----------------------------------------

  const sendWrapUp = useCallback(() => {
    if (wrapUpSentRef.current) return;
    wrapUpSentRef.current = true;
    setIsWrappingUp(true);
    breadcrumb('wrap-up-sent');
    send({
      type: 'conversation.item.create',
      item: { type: 'message', role: 'system', content: [{ type: 'input_text', text: WRAP_UP_SYSTEM_NOTE }] },
    });
    // If she is recording, the commit that follows will trigger the AI's turn anyway.
    if (!isRecordingRef.current) {
      requestResponse(WRAP_UP_INSTRUCTIONS);
    }
  }, [breadcrumb, requestResponse, send]);

  // --- Realtime events -----------------------------------------------------

  const handleRealtimeEvent = useCallback(
    (data: RealtimeEvent) => {
      const type = data.type as string;
      switch (type) {
        case 'input_audio_buffer.committed': {
          const itemId = data.item_id as string | undefined;
          if (itemId) {
            // Placeholder keeps the student's bubble in the right place even if
            // the transcript arrives after the AI's reply.
            updateTurns((prev) => [
              ...prev,
              { role: 'user', content: '', timestamp: new Date(), itemId, pending: true },
            ]);
          }
          if (awaitingCommitRef.current) {
            awaitingCommitRef.current = false;
            requestResponse();
          }
          break;
        }

        case 'conversation.item.input_audio_transcription.completed': {
          const itemId = data.item_id as string | undefined;
          const userText = ((data.transcript as string) ?? '').trim();
          resetIdle();
          if (!userText || isLikelyTranscriptionArtifact(userText)) {
            breadcrumb('user-transcript-dropped', { itemId, length: userText.length });
            updateTurns((prev) => prev.filter((turn) => turn.itemId !== itemId));
            break;
          }
          let replaced = false;
          updateTurns((prev) => {
            const next = prev.map((turn) => {
              if (itemId && turn.itemId === itemId) {
                replaced = true;
                return { ...turn, content: userText, pending: false, failed: false };
              }
              return turn;
            });
            return replaced
              ? next
              : [...next, { role: 'user' as const, content: userText, timestamp: new Date(), itemId }];
          });
          saveTurn('user', userText, itemId);
          break;
        }

        case 'conversation.item.input_audio_transcription.failed': {
          const itemId = data.item_id as string | undefined;
          Sentry.captureMessage('OpenAI Realtime: user transcription failed', {
            level: 'warning',
            tags: { feature: 'conversational-exercise', provider: 'openai' },
            extra: { sessionId: sessionIdRef.current, error: data.error },
          });
          updateTurns((prev) =>
            prev.map((turn) =>
              itemId && turn.itemId === itemId ? { ...turn, pending: false, failed: true } : turn
            )
          );
          break;
        }

        case 'response.created': {
          const response = data.response as { id?: string } | undefined;
          activeResponseIdRef.current = response?.id ?? 'active';
          setIsAiThinking(true);
          resetAiLive();
          if (nudgePendingRef.current) {
            nudgePendingRef.current = false;
            nudgeResponseIdRef.current = activeResponseIdRef.current;
          }
          break;
        }

        // Streamed words of what the AI is saying (GA name first, beta name as fallback).
        case 'response.output_audio_transcript.delta':
        case 'response.audio_transcript.delta': {
          const delta = data.delta as string | undefined;
          if (delta) pushAiLive(delta);
          break;
        }

        // GA event name is response.output_audio_transcript.done; keep the beta name as fallback
        case 'response.output_audio_transcript.done':
        case 'response.audio_transcript.done': {
          const assistantText = ((data.transcript as string) ?? '').trim();
          if (assistantText) resetAiLive(assistantText);
          const isNudge = !!nudgeResponseIdRef.current && data.response_id === nudgeResponseIdRef.current;
          if (!isNudge) resetIdle();
          if (assistantText) {
            updateTurns((prev) => [...prev, { role: 'assistant', content: assistantText, timestamp: new Date() }]);
            saveTurn('assistant', assistantText);
          }
          break;
        }

        case 'response.function_call_arguments.done':
          if ((data.name as string) === 'end_conversation') {
            handleAiEndRequest(data.call_id as string | undefined, data.arguments);
          }
          break;

        case 'response.done': {
          const response = data.response as { id?: string; status?: string; status_details?: unknown } | undefined;
          if (response?.status && response.status !== 'completed' && response.status !== 'cancelled') {
            breadcrumb('response-not-completed', { status: response.status, details: response.status_details });
          }
          if (nudgeResponseIdRef.current && response?.id === nudgeResponseIdRef.current) {
            nudgeResponseIdRef.current = null;
          }
          activeResponseIdRef.current = null;
          setIsAiThinking(false);
          if (!aiSpeakingRef.current) {
            setCurrentTranscript('');
            resolveAudioDrain();
          }
          if (responseQueuedRef.current && !endedRef.current) {
            responseQueuedRef.current = false;
            requestResponse();
          }
          break;
        }

        // GA/WebRTC event name is output_audio_buffer.started; keep the beta name as fallback
        case 'output_audio_buffer.started':
        case 'response.audio.started':
          aiSpeakingRef.current = true;
          setIsAiSpeaking(true);
          setIsAiThinking(false);
          setCurrentTranscript('AI is speaking...');
          break;

        case 'output_audio_buffer.stopped':
        case 'output_audio_buffer.cleared':
          aiSpeakingRef.current = false;
          setIsAiSpeaking(false);
          if (!activeResponseIdRef.current) setCurrentTranscript('');
          resolveAudioDrain();
          break;

        case 'input_audio_buffer.speech_started':
          setCurrentTranscript('Listening...');
          break;

        case 'input_audio_buffer.speech_stopped':
          setCurrentTranscript('Processing...');
          break;

        case 'input_audio_buffer.timeout_triggered':
          // Only fires with server VAD, which is off. Never end the call on it.
          breadcrumb('input_audio_buffer.timeout_triggered');
          break;

        case 'error': {
          const apiError = (data.error ?? {}) as Record<string, unknown>;
          const code = apiError.code as string | undefined;
          console.error('Realtime API error:', apiError);
          Sentry.captureMessage(`OpenAI Realtime API error: ${(apiError.message as string) ?? 'unknown'}`, {
            level: code === 'conversation_already_has_active_response' ? 'warning' : 'error',
            tags: { feature: 'conversational-exercise', provider: 'openai', code: code ?? 'unknown' },
            extra: { sessionId: sessionIdRef.current, apiError },
          });
          if (code === 'conversation_already_has_active_response') {
            // We asked too early; the queued request goes out on response.done.
            responseQueuedRef.current = true;
            break;
          }
          if (code === 'input_audio_buffer_commit_empty') {
            awaitingCommitRef.current = false;
            setCurrentTranscript('');
            break;
          }
          setError((apiError.message as string) ?? 'An error occurred');
          break;
        }
      }
    },
    [breadcrumb, handleAiEndRequest, requestResponse, resetIdle, resolveAudioDrain, saveTurn, updateTurns]
  );

  // --- Connect -------------------------------------------------------------

  const connect = useCallback(
    async (session: { sessionId: string; ephemeralKey: string }) => {
      const { getMicConstraints, setSpeakerOnElement } = optionsRef.current;

      // Fresh call: reset everything from a previous one.
      sessionIdRef.current = session.sessionId;
      endedRef.current = false;
      endingPromiseRef.current = null;
      pendingSavesRef.current = new Set();
      sequenceRef.current = 0;
      wrapUpSentRef.current = false;
      turnsRef.current = [];
      setTurns([]);
      setError(null);
      setIsWrappingUp(false);
      setIdleSecondsLeft(null);
      setQuietSeconds(0);
      setCurrentTranscript('');
      timeRemainingRef.current = maxDurationSec;
      setTimeRemaining(maxDurationSec);

      const stream = await navigator.mediaDevices.getUserMedia(getMicConstraints());
      mediaStreamRef.current = stream;

      // Loudness analyser (best effort — never blocks the call)
      try {
        const AudioContextCtor =
          (window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext })
            .AudioContext ??
          (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (AudioContextCtor) {
          const audioContext = new AudioContextCtor();
          const analyser = audioContext.createAnalyser();
          analyser.fftSize = 1024;
          audioContext.createMediaStreamSource(stream).connect(analyser);
          audioContextRef.current = audioContext;
          analyserRef.current = analyser;
        }
      } catch (e) {
        console.warn('Loudness analyser unavailable:', e);
      }

      // Create WebRTC peer connection
      const pc = new RTCPeerConnection();
      peerConnectionRef.current = pc;

      pc.onconnectionstatechange = () => {
        if (endedRef.current) return;
        if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
          Sentry.captureMessage('OpenAI Realtime WebRTC connection failed mid-session', {
            level: 'error',
            tags: { feature: 'conversational-exercise', provider: 'openai' },
            extra: { sessionId: session.sessionId, iceConnectionState: pc.iceConnectionState },
          });
          setError('Connection to the AI was lost. We saved what you said so far.');
          void endConversationRef.current('connection');
        } else if (pc.connectionState === 'disconnected') {
          if (disconnectTimerRef.current) clearTimeout(disconnectTimerRef.current);
          disconnectTimerRef.current = setTimeout(() => {
            if (!endedRef.current && pc.connectionState === 'disconnected') {
              setError('Connection to the AI was lost. We saved what you said so far.');
              void endConversationRef.current('connection');
            }
          }, DISCONNECT_GRACE_MS);
        } else if (pc.connectionState === 'connected' && disconnectTimerRef.current) {
          clearTimeout(disconnectTimerRef.current);
          disconnectTimerRef.current = null;
        }
      };

      // Create audio element for AI responses
      const audioEl = document.createElement('audio');
      audioEl.autoplay = true;
      audioElementRef.current = audioEl;
      await setSpeakerOnElement(audioEl);

      pc.ontrack = (event) => {
        const remoteStream = event.streams[0];
        audioEl.srcObject = remoteStream;
        // Second analyser, on the AI's voice, so the UI can draw her waveform.
        // Best effort — the audio element keeps playing regardless.
        try {
          const audioContext = audioContextRef.current;
          if (audioContext && remoteStream && !remoteAnalyserRef.current) {
            const analyser = audioContext.createAnalyser();
            analyser.fftSize = 1024;
            audioContext.createMediaStreamSource(remoteStream).connect(analyser);
            remoteAnalyserRef.current = analyser;
            void audioContext.resume().catch(() => undefined);
          }
        } catch (e) {
          console.warn('Remote loudness analyser unavailable:', e);
        }
      };

      // Add local audio track (start muted for push-to-talk)
      const audioTrack = stream.getAudioTracks()[0];
      audioTrack.enabled = false;
      pc.addTrack(audioTrack, stream);

      // Data channel for events
      const dc = pc.createDataChannel('oai-events');
      dataChannelRef.current = dc;

      dc.onopen = () => {
        // Manual push-to-talk: no server VAD. (Also configured server-side; this keeps it explicit.)
        dc.send(
          JSON.stringify({
            type: 'session.update',
            session: { type: 'realtime', audio: { input: { turn_detection: null } } },
          })
        );
      };

      dc.onmessage = (event) => {
        try {
          handleRealtimeEvent(JSON.parse(event.data));
        } catch (e) {
          console.error('Failed to parse realtime event:', e);
        }
      };

      dc.onclose = () => {
        if (endedRef.current) return;
        breadcrumb('data-channel-closed');
        setError('Connection to the AI was lost. We saved what you said so far.');
        void endConversationRef.current('connection');
      };

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      // Connect to OpenAI Realtime API using the ephemeral key (GA endpoint).
      const response = await fetch('https://api.openai.com/v1/realtime/calls', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.ephemeralKey}`,
          'Content-Type': 'application/sdp',
        },
        body: offer.sdp,
      });

      if (!response.ok) {
        const responseBody = await response.text().catch(() => '');
        throw new Error(
          `Failed to connect to OpenAI Realtime API (HTTP ${response.status}): ${responseBody.slice(0, 500)}`
        );
      }

      const answerSdp = await response.text();
      await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });

      // Countdown
      timerRef.current = setInterval(() => {
        if (endedRef.current) return;
        timeRemainingRef.current = Math.max(0, timeRemainingRef.current - 1);
        setTimeRemaining(timeRemainingRef.current);
        if (timeRemainingRef.current === WRAP_UP_AT_SEC) {
          sendWrapUp();
        }
        if (timeRemainingRef.current <= 0) {
          void endConversationRef.current('timer');
        }
      }, 1000);

      // Idle clock: counts only while nobody is talking.
      idleMsRef.current = 0;
      hasPromptedIdleRef.current = false;
      idleIntervalRef.current = setInterval(() => {
        if (endedRef.current) return;
        const aiBusy = (aiSpeakingRef.current || !!activeResponseIdRef.current) && !nudgeResponseIdRef.current;
        if (isRecordingRef.current || aiBusy) {
          setIdleSecondsLeft(null);
          // While recording, the loudness sampler owns quietSeconds (silence into an open mic).
          if (!isRecordingRef.current) setQuietSeconds(0);
          return;
        }
        idleMsRef.current += 1000;
        const idle = idleMsRef.current;
        setQuietSeconds(Math.floor(idle / 1000));
        if (idle >= IDLE_END_MS) {
          breadcrumb('idle-end', { idleMs: idle });
          void endConversationRef.current('idle');
          return;
        }
        if (idle >= IDLE_PROMPT_MS && !hasPromptedIdleRef.current) {
          hasPromptedIdleRef.current = true;
          nudgePendingRef.current = true;
          breadcrumb('idle-nudge', { idleMs: idle });
          requestResponse(IDLE_NUDGE_INSTRUCTIONS);
        }
        const msLeft = IDLE_END_MS - idle;
        setIdleSecondsLeft(msLeft <= IDLE_COUNTDOWN_VISIBLE_MS ? Math.ceil(msLeft / 1000) : null);
      }, 1000);
    },
    [breadcrumb, handleRealtimeEvent, maxDurationSec, requestResponse, sendWrapUp]
  );

  // --- Recording controls --------------------------------------------------

  const startRecording = useCallback(() => {
    if (endedRef.current) return;
    if (!dataChannelRef.current || dataChannelRef.current.readyState !== 'open') return;
    if (!mediaStreamRef.current) return;

    const audioTrack = mediaStreamRef.current.getAudioTracks()[0];
    if (!audioTrack) return;

    // Student wants to talk while the AI is still answering: stop the AI first.
    if (activeResponseIdRef.current || aiSpeakingRef.current) {
      send({ type: 'response.cancel' });
      send({ type: 'output_audio_buffer.clear' });
      responseQueuedRef.current = false;
      setIsAiThinking(false);
    }
    if (commitTimeoutRef.current) {
      clearTimeout(commitTimeoutRef.current);
      commitTimeoutRef.current = null;
    }

    audioTrack.enabled = true;
    send({ type: 'input_audio_buffer.clear' });
    recordingStartTimeRef.current = Date.now();
    isRecordingRef.current = true;
    setIsRecording(true);
    setCurrentTranscript('');
    startLevelSampling();
    resetIdle();
  }, [resetIdle, send, startLevelSampling]);

  const stopRecording = useCallback(
    (forceCancel = false): boolean => {
      isRecordingRef.current = false;
      setIsRecording(false);
      const voicedRatio = stopLevelSampling();

      if (!mediaStreamRef.current) return false;

      const audioTrack = mediaStreamRef.current.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = false;
      }

      if (!dataChannelRef.current || dataChannelRef.current.readyState !== 'open') {
        recordingStartTimeRef.current = null;
        setCurrentTranscript('');
        return false;
      }

      const recordingDuration = recordingStartTimeRef.current
        ? Date.now() - recordingStartTimeRef.current
        : 0;
      recordingStartTimeRef.current = null;

      const tooShort = recordingDuration < MIN_RECORDING_MS;
      const silent = voicedRatio !== null && voicedRatio < MIN_VOICED_RATIO;

      if (forceCancel || tooShort || silent) {
        send({ type: 'input_audio_buffer.clear' });
        breadcrumb('recording-discarded', { recordingDuration, voicedRatio, forceCancel });
        setCurrentTranscript(forceCancel ? '' : "We didn't catch that — hold the button and speak");
        return false;
      }

      // Let the last few hundred ms of audio reach the server before committing.
      awaitingCommitRef.current = true;
      setCurrentTranscript('Processing...');
      commitTimeoutRef.current = setTimeout(() => {
        commitTimeoutRef.current = null;
        if (endedRef.current) return;
        send({ type: 'input_audio_buffer.commit' });
      }, COMMIT_TAIL_MS);
      resetIdle();
      return true;
    },
    [breadcrumb, resetIdle, send, stopLevelSampling]
  );
  stopRecordingRef.current = stopRecording;

  const handleToggleRecording = useCallback(() => {
    // A hold-to-talk release is followed by a synthetic click: ignore it.
    if (Date.now() < suppressClickUntilRef.current) return;
    if (isHoldingRef.current) {
      isHoldingRef.current = false;
      return;
    }

    if (isRecordingRef.current) {
      stopRecording();
    } else {
      startRecording();
    }
  }, [startRecording, stopRecording]);

  const handlePointerDown = useCallback(() => {
    if (!dataChannelRef.current || dataChannelRef.current.readyState !== 'open') return;

    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
    }

    holdTimeoutRef.current = setTimeout(() => {
      holdTimeoutRef.current = null;
      if (!isRecordingRef.current) {
        isHoldingRef.current = true;
        startRecording();
      }
    }, 150);
  }, [startRecording]);

  const handlePointerUp = useCallback(() => {
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }

    if (isHoldingRef.current && isRecordingRef.current) {
      stopRecording();
      // Reset here, not in the click handler: if no click follows (pointer
      // left the button) the next tap must still work.
      isHoldingRef.current = false;
      suppressClickUntilRef.current = Date.now() + SUPPRESS_CLICK_MS;
    }
  }, [stopRecording]);

  const clearError = useCallback(() => setError(null), []);
  const isActive = useCallback(() => !!sessionIdRef.current && !endedRef.current, []);

  const getLevels = useCallback((): { mic: number; ai: number } => {
    const read = (analyser: AnalyserNode | null, key: 'mic' | 'ai'): number => {
      if (!analyser) return 0;
      const buffers = levelBuffersRef.current;
      let buffer = buffers[key];
      if (!buffer || buffer.length !== analyser.fftSize) {
        buffer = new Float32Array(analyser.fftSize);
        buffers[key] = buffer;
      }
      analyser.getFloatTimeDomainData(buffer);
      // Speech RMS sits roughly in 0.01–0.2; scale so a normal voice fills the bars.
      return Math.min(1, computeRms(buffer) / LEVEL_FULL_SCALE_RMS);
    };
    return {
      mic: isRecordingRef.current ? read(analyserRef.current, 'mic') : 0,
      ai: read(remoteAnalyserRef.current, 'ai'),
    };
  }, []);

  return {
    turns,
    currentTranscript,
    timeRemaining,
    isRecording,
    error,
    idleSecondsLeft,
    quietSeconds,
    isWrappingUp,
    isAiSpeaking,
    isAiThinking,
    aiLiveTranscript,
    connect,
    endConversation,
    cleanup,
    clearError,
    isActive,
    handleToggleRecording,
    handlePointerDown,
    handlePointerUp,
    getLevels,
  };
}
